import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useAddItem } from "@/context/addItem";

const CATEGORIES = [
  "Dairy",
  "Fruits",
  "Vegetables",
  "Meat",
  "Seafood",
  "Bakery",
  "Pantry",
  "Frozen",
  "Snacks",
  "Beverages",
  "Condiments",
  "Spices",
  "Canned Goods",
  "Household",
  "Personal Care",
  "Other"
];

const LOCATIONS = [
  "Fridge",
  "Freezer",
  "Pantry",
  "Cabinet",
  "Countertop",
  "Other"
];

const ManualEntryTab = () => {
  const { 
    formData, 
    errors, 
    pendingItems,
    handleFormChange
  } = useAddItem();
  
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      handleFormChange("expiration", date);
    }
  };
  
  return (
    <div className="grid gap-6">
      {pendingItems.length > 1 && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800">
          <p className="font-medium">Multiple items detected!</p>
          <p>You're adding item {pendingItems.indexOf(pendingItems.find(i => i.name === formData.name) || pendingItems[0]) + 1} of {pendingItems.length}</p>
        </div>
      )}
      
      <div className="grid gap-3">
        <Label htmlFor="name" className={cn(errors.name && "text-red-500")}>
          Item Name*
        </Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => handleFormChange("name", e.target.value)}
          placeholder="Enter item name"
          className={cn(errors.name && "border-red-500")}
        />
        {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
      </div>
      
      <div className="grid gap-3">
        <Label htmlFor="category" className={cn(errors.category && "text-red-500")}>
          Category*
        </Label>
        <Select 
          value={formData.category}
          onValueChange={(value) => handleFormChange("category", value)}
        >
          <SelectTrigger className={cn(errors.category && "border-red-500")}>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.category && <p className="text-sm text-red-500">{errors.category}</p>}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-3">
          <Label htmlFor="quantity" className={cn(errors.quantity && "text-red-500")}>
            Quantity*
          </Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            value={formData.quantity}
            onChange={(e) => handleFormChange("quantity", parseInt(e.target.value))}
            className={cn(errors.quantity && "border-red-500")}
          />
          {errors.quantity && <p className="text-sm text-red-500">{errors.quantity}</p>}
        </div>
        
        <div className="grid gap-3">
          <Label htmlFor="location">
            Location
          </Label>
          <Select 
            value={formData.location}
            onValueChange={(value) => handleFormChange("location", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent>
              {LOCATIONS.map((location) => (
                <SelectItem key={location} value={location}>
                  {location}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid gap-3">
        <Label htmlFor="expiration" className={cn(errors.expiration && "text-red-500")}>
          Expiration Date*
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="expiration"
              variant={"outline"}
              className={cn(
                "justify-start text-left font-normal",
                !formData.expiration && "text-muted-foreground",
                errors.expiration && "border-red-500"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formData.expiration ? (
                formData.expiration instanceof Date ? 
                  format(formData.expiration, "PPP") : 
                  format(new Date(formData.expiration), "PPP")
              ) : (
                <span>Pick a date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={
                formData.expiration instanceof Date ? 
                  formData.expiration : 
                  formData.expiration ? new Date(formData.expiration as string) : undefined
              }
              onSelect={handleDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {errors.expiration && <p className="text-sm text-red-500">{errors.expiration}</p>}
      </div>
      
      <div className="grid gap-3">
        <Label htmlFor="notes">
          Notes
        </Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleFormChange("notes", e.target.value)}
          placeholder="Add any additional information about this item"
          className="resize-none"
        />
      </div>
    </div>
  );
};

export default ManualEntryTab;
